import { useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

export interface LeadResult {
  id: string
  name: string
  country: string
  city: string | null
  type: string | null
  employees: number | null
  revenue: number | null
  services: string[] | null
}

export interface AgentResponse {
  success: boolean
  question: string
  explanation: string
  confidence: 'high' | 'medium' | 'low'
  sql: string
  results: LeadResult[]
  count: number
  error?: string
}

export interface ChatMessage {
  id: string
  type: 'user' | 'agent'
  content: string
  timestamp: Date
  response?: AgentResponse
  isLoading?: boolean
}

export function useSalesAgent() {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const askQuestion = useCallback(async (question: string) => {
    const userMessageId = crypto.randomUUID()
    const agentMessageId = crypto.randomUUID()

    // Add user message
    const userMessage: ChatMessage = {
      id: userMessageId,
      type: 'user',
      content: question,
      timestamp: new Date(),
    }

    // Add loading agent message
    const loadingMessage: ChatMessage = {
      id: agentMessageId,
      type: 'agent',
      content: '',
      timestamp: new Date(),
      isLoading: true,
    }

    setMessages(prev => [...prev, userMessage, loadingMessage])
    setIsLoading(true)

    try {
      const { data, error } = await supabase.functions.invoke('sales-agent', {
        body: { question },
      })

      if (error) {
        throw error
      }

      const response = data as AgentResponse

      // Update the loading message with the response
      setMessages(prev =>
        prev.map(msg =>
          msg.id === agentMessageId
            ? {
                ...msg,
                content: response.explanation || response.error || 'Bir hata oluştu',
                response: response.success ? response : undefined,
                isLoading: false,
              }
            : msg
        )
      )

      return response
    } catch (error) {
      console.error('Sales agent error:', error)

      // Update the loading message with error
      setMessages(prev =>
        prev.map(msg =>
          msg.id === agentMessageId
            ? {
                ...msg,
                content: 'Üzgünüm, bir hata oluştu. Lütfen tekrar deneyin.',
                isLoading: false,
              }
            : msg
        )
      )

      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  const clearMessages = useCallback(() => {
    setMessages([])
  }, [])

  return {
    messages,
    isLoading,
    askQuestion,
    clearMessages,
  }
}
